import { type TzMode, prettyDateTime } from "@app/date";
import {
  cancelOrgOperationsPoll,
  getResourceUrl,
  pollOrgOperations,
  prettyResourceType,
  selectAppById,
  selectDatabaseById,
  selectEndpointById,
  selectServiceById,
} from "@app/deploy";
import {
  type FreezerActivity,
  type FreezerPaginationMeta,
  fetchActivityAccounts,
  selectFreezerFilterAccountsWithStatus,
} from "@app/freezer";
import { selectNav } from "@app/nav";
import { selectOrganizationSelected } from "@app/organizations";
import { useLoader, useQuery, useSelector } from "@app/react";
import {
  appDetailUrl,
  databaseDetailUrl,
  operationDetailUrl,
} from "@app/routes";
import { humanize } from "@app/string-utils";
import { tunaEvent } from "@app/tuna";
import type { DeployActivityRow, DeployOperation } from "@app/types";
import { type CalendarDate, today } from "@internationalized/date";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RangeValue } from "react-aria-components";
import { Link, useSearchParams } from "react-router-dom";
import {
  type AppliedExportFilters,
  type PaginateProps,
  useActivityExport,
  useActivityFilters,
  usePaginatedOpsByOrgId,
  useSyncedQueryParam,
} from "../hooks";
import { usePoller } from "../hooks/use-poller";
import {
  ActivityDataTable,
  ColumnMenu,
  DEFAULT_COLUMN_ORDER,
  DEFAULT_COLUMN_VISIBILITY,
  TOGGLEABLE_COLUMNS,
} from "./activity-table";
import { Banner } from "./banner";
import { Box } from "./box";
import { Button } from "./button";
import { ExportBanner, type ExportBannerStatus } from "./export-banner";
import { ExportButton } from "./export-button";
import { Label } from "./form-group";
import { Group } from "./group";
import { IconInfo, type IconProps, IconX, IconXCircle } from "./icons";
import { InputDateRange, InputSearch } from "./input";
import { LoadingSpinner } from "./loading";
import { MultiSelect } from "./multi-select";
import { OpStatus } from "./operation-status";
import { getImageForResourceType } from "./resource-icons";
import {
  DescBar,
  FilterBar,
  PaginateBar,
  TitleBar,
} from "./resource-list-view";
import { EnvStackCell } from "./resource-table";
import { Select, type SelectOption } from "./select";
import { Switch } from "./switch";
import { EmptyTr, TBody, THead, Table, Td, Th, Tr } from "./table";
import { tokens } from "./tokens";
import { Tooltip } from "./tooltip";

interface OpCellProps {
  op: DeployActivityRow;
}

export const AppConfigureTooltip = ({ op }: { op: DeployOperation }) => {
  if (op.type !== "configure") return null;
  const text = Object.keys(op.env).join(", ");
  return (
    <Tooltip text={text}>
      <IconInfo variant="sm" />
    </Tooltip>
  );
};

const OpTypeCell = ({ op }: OpCellProps) => {
  return (
    <Td className="flex-1">
      <Group variant="horizontal" className="items-center" size="xs">
        <Link
          to={operationDetailUrl(op.id)}
          className={tokens.type["table link"]}
        >
          {humanize(op.type)}
        </Link>
        <AppConfigureTooltip op={op} />
      </Group>
      <div>ID: {op.id}</div>
    </Td>
  );
};

const OpStatusCell = ({ op }: OpCellProps) => {
  return (
    <Td>
      <OpStatus status={op.status} />
    </Td>
  );
};

const OpResourceCell = ({ op }: OpCellProps) => {
  const url = getResourceUrl(op);
  return (
    <Td>
      <div className="flex">
        {getImageForResourceType(op.resourceType)}
        <div>
          {url ? (
            <Link to={url} className={tokens.type["table link"]}>
              {op.resourceHandle}
            </Link>
          ) : (
            <div>{op.resourceHandle}</div>
          )}
          {op.resourceDesc ? <div>{op.resourceDesc}</div> : null}
          <div>{prettyResourceType(op.resourceType)}</div>
        </div>
      </div>
    </Td>
  );
};

const OpServiceCell = ({ serviceId }: { serviceId: string }) => {
  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  const app = useSelector((s) => selectAppById(s, { id: service.appId }));
  const db = useSelector((s) =>
    selectDatabaseById(s, { id: service.databaseId }),
  );

  return (
    <Td>
      <Link
        className={tokens.type["table link"]}
        to={
          service.appId
            ? appDetailUrl(service.appId)
            : databaseDetailUrl(service.databaseId)
        }
      >
        {service.appId ? app.handle : db.handle}
      </Link>
    </Td>
  );
};

export const OpEndpointCell = ({ enpId }: { enpId: string }) => {
  const enp = useSelector((s) => selectEndpointById(s, { id: enpId }));
  return <OpServiceCell serviceId={enp.serviceId} />;
};

const OpParentResourceCell = ({ op }: OpCellProps) => {
  if (op.resourceType === "service") {
    return <OpServiceCell serviceId={op.resourceId} />;
  }

  if (op.resourceType === "vhost") {
    return <OpEndpointCell enpId={op.resourceId} />;
  }

  return <Td> </Td>;
};

const OpActionsCell = ({ op }: OpCellProps) => {
  return (
    <Td>
      <Link
        to={operationDetailUrl(op.id)}
        className="hover:no-underline flex justify-end mr-4"
      >
        <Button variant="primary" size="sm">
          Logs
        </Button>
      </Link>
    </Td>
  );
};

const OpLastUpdatedCell = ({ op }: OpCellProps) => {
  return (
    <Td>
      <div>{prettyDateTime(op.updatedAt)}</div>
    </Td>
  );
};

const OpUserCell = ({ op }: OpCellProps) => {
  return (
    <Td>
      <div>{op.userName}</div>
      {op.note ? (
        <Tooltip fluid text={op.note}>
          <div className="max-w-[300px] truncate">Note: {op.note}</div>
        </Tooltip>
      ) : null}
    </Td>
  );
};

const OpListRow = ({ op }: OpCellProps) => {
  return (
    <Tr>
      <OpResourceCell op={op} />
      <OpStatusCell op={op} />
      <OpTypeCell op={op} />
      <OpParentResourceCell op={op} />
      <EnvStackCell environmentId={op.environmentId} />
      <OpUserCell op={op} />
      <OpLastUpdatedCell op={op} />
      <OpActionsCell op={op} />
    </Tr>
  );
};

const IconXCircleDeleted = (props: IconProps) => {
  return (
    <IconXCircle
      variant="sm"
      title="Deleted"
      className="stroke-black fill-red-300"
      {...props}
    />
  );
};

const LEGACY_OPERATION_STATUSES = ["queued", "running", "failed", "succeeded"];
const LEGACY_OPERATION_TYPES = [
  "backup",
  "configure",
  "configure_backup_policy",
  "copy",
  "create",
  "deploy",
  "deprovision",
  "execute",
  "horizontal_scale",
  "logs",
  "modify",
  "provision",
  "purge",
  "rebuild",
  "reload",
  "renew",
  "replicate",
  "revoke",
  "restart",
  "restore",
  "scale",
  "scan",
  "service_deployment_update",
];
const LEGACY_RESOURCE_TYPES = [
  { label: "App", value: "App" },
  { label: "Backup", value: "Backup" },
  { label: "Certificate", value: "Certificate" },
  { label: "Configuration", value: "Configuration" },
  { label: "Database", value: "Database" },
  { label: "DatabaseCredential", value: "DatabaseCredential" },
  { label: "EphemeralSession", value: "EphemeralSession" },
  { label: "Environment", value: "Account" },
  { label: "LLM Key", value: "LlmKey" },
  { label: "Image", value: "Image" },
  { label: "LogDrain", value: "LogDrain" },
  { label: "MetricDrain", value: "MetricDrain" },
  { label: "Service", value: "Service" },
  { label: "Vhost", value: "Vhost" },
];

function ActivityTable({
  paginated,
  showTitle = true,
  isLoading = false,
  showResourceTypeFilter = false,
}: {
  paginated: PaginateProps<DeployActivityRow> & { isLoading: boolean };
  showTitle?: boolean;
  isLoading?: boolean;
  showResourceTypeFilter?: boolean;
}) {
  const FILTER_ALL = "all";
  const [params, setParams] = useSearchParams();

  const resourceType = params.get("resource_type") || FILTER_ALL;
  const operationType = params.get("operation_type") || FILTER_ALL;
  const operationStatus = params.get("operation_status") || FILTER_ALL;

  const operationStatusesAsOptions = [
    { label: "All", value: FILTER_ALL },
    ...LEGACY_OPERATION_STATUSES.map((status) => ({
      label: humanize(status),
      value: status,
    })),
  ];
  const operationTypesAsOptions = [
    { label: "All", value: FILTER_ALL },
    ...LEGACY_OPERATION_TYPES.map((type) => ({
      label: humanize(type),
      value: type,
    })),
  ];
  const resourceTypeAsOptions = [
    { label: "All", value: FILTER_ALL },
    ...LEGACY_RESOURCE_TYPES,
  ];
  const [resourceTypeFilter, setResourceTypeFilter] = useState(resourceType);
  const [operationTypeFilter, setOperationTypeFilter] = useState(operationType);
  const [operationStatusFilter, setOperationStatusFilter] =
    useState(operationStatus);
  const onFilter = () => {
    setParams({
      resource_type: resourceTypeFilter,
      operation_type: operationTypeFilter,
      operation_status: operationStatusFilter,
    });
  };
  const onReset = () => {
    setResourceTypeFilter(FILTER_ALL);
    setOperationTypeFilter(FILTER_ALL);
    setOperationStatusFilter(FILTER_ALL);
    setParams({});
  };

  return (
    <Group>
      <Group size="sm">
        <TitleBar
          visible={showTitle}
          description="This global Activity page show real-time changes to resources within the last week."
        >
          Activity
        </TitleBar>

        <FilterBar>
          {showTitle ? null : (
            <DescBar>
              Operations show real-time changes to resources, such as Apps and
              Databases.
            </DescBar>
          )}

          <Group variant="horizontal" size="lg" className="items-center mt-1">
            <DescBar>{paginated.totalItems} Operations</DescBar>
            <PaginateBar
              {...paginated}
              isLoading={isLoading || paginated.isLoading}
            />
          </Group>
        </FilterBar>
      </Group>

      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onFilter();
          }}
        >
          <Group>
            <Group variant="horizontal">
              {showResourceTypeFilter ? (
                <div className="flex-1">
                  <Label htmlFor="resource-type-selector">Resource Type</Label>
                  <Select
                    id="resource-type-selector"
                    value={resourceTypeFilter}
                    options={resourceTypeAsOptions}
                    onSelect={(opt) => setResourceTypeFilter(opt.value)}
                    className="w-full"
                  />
                </div>
              ) : null}

              <div className="flex-1">
                <Label htmlFor="operation-type-selector">Operation Type</Label>
                <Select
                  id="operation-type-selector"
                  value={operationTypeFilter}
                  options={operationTypesAsOptions}
                  onSelect={(opt) => setOperationTypeFilter(opt.value)}
                  className="w-full"
                />
              </div>

              <div className="flex-1">
                <Label htmlFor="operation-status-selector">
                  Operation Status
                </Label>
                <Select
                  id="operation-status-selector"
                  options={operationStatusesAsOptions}
                  value={operationStatusFilter}
                  onSelect={(opt) => setOperationStatusFilter(opt.value)}
                  className="w-full"
                />
              </div>
            </Group>

            <hr />

            <Group
              variant="horizontal"
              className="items-center justify-between"
            >
              <Group variant="horizontal" size="sm">
                <Button type="submit">Filter</Button>
                <Button variant="white" onClick={onReset}>
                  Reset
                </Button>
              </Group>
            </Group>
          </Group>
        </form>
      </Box>

      <Table>
        <THead>
          <Th>Resource</Th>
          <Th>Status</Th>
          <Th>Type</Th>
          <Th>Related To</Th>
          <Th>Environment</Th>
          <Th>User</Th>
          <Th>Last Updated</Th>
          <Th variant="right">Actions</Th>
        </THead>

        <TBody>
          {paginated.data.length === 0 ? <EmptyTr colSpan={8} /> : null}
          {paginated.data.map((op) => (
            <OpListRow op={op} key={op.id} />
          ))}
        </TBody>
      </Table>
      {paginated.totalPages > 1 && (
        <PaginateBar
          {...paginated}
          isLoading={isLoading || paginated.isLoading}
        />
      )}
    </Group>
  );
}

function getDefaultDateRange(): RangeValue<CalendarDate> {
  const todayDate = today("UTC");
  return {
    start: todayDate.subtract({ days: 6 }),
    end: todayDate,
  };
}

export function ActivityByOrgV2({ orgId }: { orgId: string }) {
  const result = useActivityFilters({ orgId });

  // Env-specific: fetch accounts and build env filter UI
  useQuery(fetchActivityAccounts());
  const freezerAccounts = useSelector(selectFreezerFilterAccountsWithStatus);
  const envOptions = useMemo(
    () =>
      freezerAccounts
        .map((acct) => ({ value: acct.value, label: acct.title }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [freezerAccounts],
  );
  const deletedEnvIds = useMemo(
    () => new Set(freezerAccounts.filter((a) => a.deleted).map((a) => a.value)),
    [freezerAccounts],
  );
  const hasAnyDeletedEnv = deletedEnvIds.size > 0;
  const renderEnvOptionLabel = useCallback(
    (option: SelectOption) => {
      const isDeleted = deletedEnvIds.has(option.value);
      if (!hasAnyDeletedEnv) return option.label;
      return (
        <span className="flex items-center gap-1.5 w-full">
          <span className="w-4 shrink-0 flex items-center justify-center">
            {isDeleted ? <IconXCircleDeleted /> : null}
          </span>
          <span>{option.label}</span>
        </span>
      );
    },
    [deletedEnvIds, hasAnyDeletedEnv],
  );
  const getEnvOptionTitle = useCallback(
    (option: SelectOption) => {
      return deletedEnvIds.has(option.value)
        ? "This environment has been deleted"
        : undefined;
    },
    [deletedEnvIds],
  );

  return (
    <ActivityTableFreezer
      {...result}
      envOptions={envOptions}
      renderEnvOptionLabel={renderEnvOptionLabel}
      getEnvOptionTitle={getEnvOptionTitle}
    />
  );
}

export function ActivityByEnvV2({ envId }: { envId: string }) {
  const org = useSelector(selectOrganizationSelected);
  const result = useActivityFilters({
    orgId: org.id,
    fixedFilters: { envs: [envId] },
  });
  return <ActivityTableFreezer {...result} showTitle={false} />;
}

export function ActivityByAppV2({ appId }: { appId: string }) {
  const org = useSelector(selectOrganizationSelected);
  const app = useSelector((s) => selectAppById(s, { id: appId }));
  const envId = app.environmentId;
  const result = useActivityFilters({
    orgId: org.id,
    fixedFilters: {
      resourceType: "App",
      resourceId: appId,
      envs: envId ? [envId] : [],
    },
  });
  return <ActivityTableFreezer {...result} showTitle={false} />;
}

export function ActivityByDatabaseV2({ dbId }: { dbId: string }) {
  const org = useSelector(selectOrganizationSelected);
  const db = useSelector((s) => selectDatabaseById(s, { id: dbId }));
  const envId = db.environmentId;
  const result = useActivityFilters({
    orgId: org.id,
    fixedFilters: {
      resourceType: "Database",
      resourceId: dbId,
      envs: envId ? [envId] : [],
    },
  });
  return <ActivityTableFreezer {...result} showTitle={false} />;
}

export function ActivityByEndpointV2({ enpId }: { enpId: string }) {
  const org = useSelector(selectOrganizationSelected);
  const enp = useSelector((s) => selectEndpointById(s, { id: enpId }));
  const service = useSelector((s) =>
    selectServiceById(s, { id: enp.serviceId }),
  );
  const envId = service.environmentId;
  const result = useActivityFilters({
    orgId: org.id,
    fixedFilters: {
      resourceType: "Endpoint",
      resourceId: enpId,
      envs: envId ? [envId] : [],
    },
  });
  return <ActivityTableFreezer {...result} showTitle={false} />;
}

const ActivitySearchBox = () => {
  const { search, onChange } = useSyncedQueryParam(
    "q",
    650,
    "activity-filter-query",
  );
  const collapsed = useSelector(selectNav).collapsed;

  const clearSearch = () => {
    onChange({
      currentTarget: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  // Hide search bar:
  // - If sidebar collapsed: hide when viewport <= 860px
  // - If sidebar expanded: hide when viewport <= 1040px
  const hideClasses = collapsed ? "max-[860px]:hidden" : "max-[1040px]:hidden";

  return (
    <div
      className={`relative w-full sm:w-[250px] h-[36px] min-w-[180px] flex-shrink-0 ${hideClasses}`}
    >
      <InputSearch
        search={search}
        onChange={onChange}
        placeholder="Search activity..."
        className="w-full h-full"
      />
      {search.trim() !== "" ? (
        <button
          type="button"
          aria-label="Clear search"
          className="absolute top-1/2 -translate-y-1/2 right-[10px] text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-inset rounded"
          onClick={clearSearch}
        >
          <IconX variant="sm" color="#6B7280" />
        </button>
      ) : null}
    </div>
  );
};

export function ActivityTableFreezer({
  activities,
  isLoading,
  dateRange,
  onDateRangeChange,
  pagination,
  onPageChange,
  dateWarning,
  onDismissWarning,
  typeOptions,
  statusOptions,
  actorOptions,
  envOptions = [],
  renderEnvOptionLabel,
  getEnvOptionTitle,
  stagedTypes,
  stagedStatuses,
  stagedActors,
  stagedEnvs = [],
  stagedReadOnly,
  onTypesChange,
  onStatusesChange,
  onActorsChange,
  onEnvsChange,
  onReadOnlyChange,
  onApplyFilters,
  onResetFilters,
  hasPendingChanges,
  showEnvFilter = true,
  hideFilters = false,
  hidePagination = false,
  showTitle = true,
  initialColumnVisibility,
  appliedExportFilters,
}: {
  activities: FreezerActivity[];
  isLoading: boolean;
  dateRange: RangeValue<CalendarDate>;
  onDateRangeChange: (range: RangeValue<CalendarDate> | null) => void;
  pagination: FreezerPaginationMeta;
  onPageChange: (page: number) => void;
  dateWarning: string | null;
  onDismissWarning: () => void;
  typeOptions: SelectOption[];
  statusOptions: SelectOption[];
  actorOptions: SelectOption[];
  envOptions?: SelectOption[];
  renderEnvOptionLabel?: (option: SelectOption) => React.ReactNode;
  getEnvOptionTitle?: (option: SelectOption) => string | undefined;
  stagedTypes: string[];
  stagedStatuses: string[];
  stagedActors: string[];
  stagedEnvs?: string[];
  stagedReadOnly: boolean;
  onTypesChange: (values: string[]) => void;
  onStatusesChange: (values: string[]) => void;
  onActorsChange: (values: string[]) => void;
  onEnvsChange?: (values: string[]) => void;
  onReadOnlyChange: (value: boolean) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  hasPendingChanges: boolean;
  showEnvFilter?: boolean;
  hideFilters?: boolean;
  hidePagination?: boolean;
  showTitle?: boolean;
  initialColumnVisibility?: Record<string, boolean>;
  appliedExportFilters?: AppliedExportFilters;
}) {
  const [params] = useSearchParams();
  const q = params.get("q") || undefined;
  const [tz, setTz] = useState<TzMode>("utc");

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        tunaEvent("key-f-triggered-on-activity-table");
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const finalTypeOptions = useMemo(() => {
    return typeOptions.map((option) => ({
      ...option,
      disabled: !stagedReadOnly && option.readOnly === true,
    }));
  }, [typeOptions, stagedReadOnly]);

  const columns = showEnvFilter
    ? TOGGLEABLE_COLUMNS
    : TOGGLEABLE_COLUMNS.map((c) => {
        if (c.id !== "environment") return c;
        return { ...c, hidden: true };
      });
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({
    ...DEFAULT_COLUMN_VISIBILITY,
    ...(!showEnvFilter && { environment: false }),
    ...initialColumnVisibility,
  });
  const [columnOrder, setColumnOrder] =
    useState<string[]>(DEFAULT_COLUMN_ORDER);

  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const hasNonDefaultDateRange =
    dateRange.start.compare(defaultRange.start) !== 0 ||
    dateRange.end.compare(defaultRange.end) !== 0;

  const hasActiveFilters =
    stagedTypes.length > 0 ||
    stagedStatuses.length > 0 ||
    stagedActors.length > 0 ||
    (showEnvFilter && stagedEnvs.length > 0) ||
    hasNonDefaultDateRange ||
    stagedReadOnly;

  const paginateProps = useMemo(
    () => ({
      page: pagination.page,
      totalPages: pagination.totalPages,
      isLoading,
      prev: () => {
        if (pagination.page > 1) {
          onPageChange(pagination.page - 1);
        }
      },
      next: () => {
        if (pagination.page < pagination.totalPages) {
          onPageChange(pagination.page + 1);
        }
      },
    }),
    [pagination, isLoading, onPageChange],
  );

  const defaultExportFilters: AppliedExportFilters = useMemo(
    () => ({
      organizationId: "",
      occurredAfter: "",
      occurredBefore: "",
    }),
    [],
  );
  const exportHook = useActivityExport(
    appliedExportFilters || defaultExportFilters,
  );
  const exportBannerStatus: ExportBannerStatus =
    exportHook.status === "error"
      ? "error"
      : exportHook.status === "ready"
        ? "ready"
        : exportHook.status === "idle"
          ? "idle"
          : "exporting";

  return (
    <Group>
      {showTitle ? (
        <Group size="sm">
          <TitleBar
            visible
            description="Activity shows real-time changes across your organization."
          >
            Activity
          </TitleBar>
        </Group>
      ) : null}

      {dateWarning ? (
        <Banner variant="warning">
          <div className="flex items-center justify-between w-full">
            <span>{dateWarning}</span>
            <button
              type="button"
              onClick={onDismissWarning}
              className="ml-4 p-1 hover:bg-yellow-200 rounded"
              aria-label="Dismiss warning"
            >
              <IconX variant="sm" />
            </button>
          </div>
        </Banner>
      ) : null}

      <ExportBanner
        status={exportBannerStatus}
        onCancel={exportHook.cancel}
        errorMessage={exportHook.error}
        progress={exportHook.exportData.progress}
        downloadUrl={exportHook.downloadUrl}
      />

      {hideFilters ? null : (
        <>
          <div className={"flex flex-wrap gap-3 lg:gap-4 w-full max-w-full"}>
            <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto">
              <Label htmlFor="date-range-activity-selector">Date Range</Label>
              <InputDateRange
                id="date-range-activity-selector"
                useUTC
                value={dateRange}
                onChange={onDateRangeChange}
                className="w-full"
              />
            </div>

            <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto">
              <Label htmlFor="operation-type-selector">Operation Type</Label>
              <MultiSelect
                id="operation-type-selector"
                ariaLabel="Operation Type"
                value={stagedTypes}
                options={finalTypeOptions}
                onChange={(values) => onTypesChange(values)}
                placeholder="All Operation Types"
                className="w-full"
                customContent={
                  <div className="flex items-center gap-1.5">
                    <Switch
                      checked={stagedReadOnly}
                      onChange={onReadOnlyChange}
                      ariaLabel="Show Read-Only"
                    />
                    <span className="text-sm text-gray-700">
                      Show Read-Only
                    </span>
                    <Tooltip
                      text="Read-Only activities tend to be very high volume, and are hidden by default."
                      placement="top"
                    >
                      <IconInfo
                        variant="sm"
                        className="opacity-50 hover:opacity-100"
                      />
                    </Tooltip>
                  </div>
                }
              />
            </div>

            <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto">
              <Label htmlFor="status-selector">Status</Label>
              <MultiSelect
                id="status-selector"
                ariaLabel="Status"
                value={stagedStatuses}
                options={statusOptions}
                onChange={(values) => onStatusesChange(values)}
                placeholder="All Statuses"
                className="w-full"
              />
            </div>

            <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto">
              <Label htmlFor="actor-selector">Actor</Label>
              <MultiSelect
                id="actor-selector"
                ariaLabel="Actor"
                value={stagedActors}
                options={actorOptions}
                onChange={(values) => onActorsChange(values)}
                placeholder="All Actors"
                className="w-full"
              />
            </div>

            {showEnvFilter ? (
              <div className="flex-1 max-w-full basis-[calc(33.333%-0.75rem)] xl:basis-auto">
                <Label htmlFor="environment-selector">Environment</Label>
                <MultiSelect
                  id="environment-selector"
                  ariaLabel="Environment"
                  value={stagedEnvs}
                  options={envOptions}
                  onChange={(values) => onEnvsChange?.(values)}
                  placeholder="All Environments"
                  className="w-full"
                  renderOptionLabel={renderEnvOptionLabel}
                  getOptionTitle={getEnvOptionTitle}
                  dropdownClassName="min-w-[350px] right-0"
                />
              </div>
            ) : null}

            <div className="flex items-end gap-2 pb-0.5 flex-shrink-0 basis-full xl:basis-auto">
              {hasActiveFilters ? (
                <Button variant="white" size="sm" onClick={onResetFilters}>
                  Reset
                </Button>
              ) : null}
              {hasPendingChanges ? (
                <Tooltip
                  text="You have unapplied filter changes"
                  placement="top"
                  fluid
                >
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onApplyFilters}
                    className="shadow-lg shadow-yellow-400/50 ring-2 ring-yellow-400/60"
                  >
                    Apply Filters
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onApplyFilters}
                  disabled
                >
                  Apply Filters
                </Button>
              )}
            </div>
          </div>

          <hr />

          <Group variant="horizontal" size="lg" className={"items-center mt-1"}>
            <DescBar className="w-full">
              <div className="flex w-full items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <PaginateBar {...{ ...paginateProps, isLoading: false }} />
                  {isLoading && activities.length === 0
                    ? "Loading results..."
                    : `${pagination.totalCount || activities.length} Results`}
                  {isLoading && activities.length > 0 ? (
                    <LoadingSpinner />
                  ) : null}
                </div>
                <div className="flex items-center gap-4">
                  <ActivitySearchBox />
                  <span
                    className={`text-xs ${tz === "utc" ? "text-black font-medium" : "text-gray-400"}`}
                  >
                    UTC
                  </span>
                  <Switch
                    ariaLabel="Toggle timezone"
                    checked={tz === "local"}
                    onChange={() =>
                      setTz((prev) => (prev === "utc" ? "local" : "utc"))
                    }
                  />
                  <span
                    className={`text-xs ${tz === "local" ? "text-black font-medium" : "text-gray-400"}`}
                  >
                    Local
                  </span>
                  <ExportButton
                    onExport={exportHook.startExport}
                    isExporting={exportBannerStatus === "exporting"}
                    disabled={activities.length === 0}
                  />
                  <ColumnMenu
                    columns={columns}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                    columnOrder={columnOrder}
                    onColumnOrderChange={setColumnOrder}
                  />
                </div>
              </div>
            </DescBar>
          </Group>
        </>
      )}

      <div className="min-w-0">
        <ActivityDataTable
          activities={activities}
          isLoading={isLoading}
          tz={tz}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          hasPendingChanges={hasPendingChanges}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          q={q}
        />
      </div>
      {!hidePagination && paginateProps.totalPages > 1 && (
        <PaginateBar {...{ ...paginateProps, isLoading: false }} />
      )}
    </Group>
  );
}

export function ActivityByOrg({ orgId }: { orgId: string }) {
  const poller = useMemo(() => pollOrgOperations({ orgId }), [orgId]);
  const cancel = useMemo(() => cancelOrgOperationsPoll(), []);
  usePoller({
    action: poller,
    cancel,
  });
  const loader = useLoader(pollOrgOperations);
  const paginated = usePaginatedOpsByOrgId(orgId);

  return (
    <ActivityTable
      paginated={paginated}
      isLoading={loader.isLoading}
      showTitle
      showResourceTypeFilter
    />
  );
}
